import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { TabNodeView } from './tab-node-view'
import { nanoid } from 'nanoid'
import { IconCategory, IconType } from '@deepguide-ai/types'

export interface TabAttributes {
  id?: string
  title?: string
  icon?: string
  iconType?: IconType
  iconCategory?: IconCategory
  iconColor?: string
  showIcon?: boolean
}


export const TabSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    id: { default: nanoid(10) },
    label: { default: null },
    icon: { default: null },
    iconType: { default: null },
    iconCategory: { default: null },
    iconColor: { default: null },
    showIcon: { default: false },
  },
  toDOM() { return ['div', { 'data-tab': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-tab]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tab: {
      /**
       * Insert a tab
       */
      insertTab: (attributes?: TabAttributes) => ReturnType
      /**
       * Update tab attributes
       */
      updateTab: (attributes: Partial<TabAttributes>) => ReturnType
    }
  }
}

export const TabExtension = Node.create({
  name: 'Tab',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      title: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      icon: {
        default: 'sparkles',
        parseHTML: (element) => element.getAttribute('data-icon'),
        renderHTML: (attributes) => {
          if (!attributes.icon) {
            return {}
          }
          return {
            'data-icon': attributes.icon,
          }
        },
      },
      iconType: {
        default: 'REGULAR',
        parseHTML: (element) => element.getAttribute('data-icon-type'),
        renderHTML: (attributes) => {
          if (!attributes.iconType) {
            return {}
          }
          return {
            'data-icon-type': attributes.iconType,
          }
        },
      },
      iconCategory: {
        default: 'LUCIDE',
        parseHTML: (element) => element.getAttribute('data-icon-category'),
        renderHTML: (attributes) => {
          if (!attributes.iconCategory) {
            return {}
          }
          return {
            'data-icon-category': attributes.iconCategory,
          }
        },
      },
      iconColor: {
        default: '#3b82f6',
        parseHTML: (element) => element.getAttribute('data-icon-color'),
        renderHTML: (attributes) => {
          if (!attributes.iconColor) {
            return {}
          }
          return {
            'data-icon-color': attributes.iconColor,
          }
        },
      },
      showIcon: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-show-icon') === 'true',
        renderHTML: (attributes) => {
          if (attributes.showIcon === undefined) {
            return {}
          }
          return {
            'data-show-icon': attributes.showIcon,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Tab"]',
      },
      {
        tag: 'div.tab-element',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Tab',
        class: 'tab-element mb-1 p-0',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(TabNodeView)
  },

  addCommands() {
    return {
      insertTab:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: '',
              icon: 'sparkles',
              iconType: 'REGULAR',
              iconCategory: 'LUCIDE',
              iconColor: '#3b82f6',
              showIcon: false,
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  
                ],
              },
            ],
          })
        },
      updateTab:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the tab with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a tab
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
